<template>
  <div
    v-bem="{
      hasErrors: context.visibleValidationErrors.length,
      hasFiles: files.length,
    }"
  >
    <div ref="dropzone" v-bem:dropzone />
    <div ref="previews" v-bem:files />
    <div v-bem:background>
      <div v-bem:backgroundText>
        <span>Drag & Drop oder</span>
      </div>
      <div v-bem:trigger>
        <button class="btn btn-sm btn-primary" @click.prevent>
          {{ buttonText }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Dropzone from 'dropzone';
import { isArray } from 'lodash-es';

Dropzone.autoDiscover = false;

const defaultDropzoneOptions = {
  dictRemoveFile: 'Datei entfernen',
  dictCancelUpload: 'Upload abbrechen',
  dictInvalidFileType: 'Dieser Dateityp darf hier nicht hochgeladen werden.',
  dictFileTooBig:
    'Deine Datei ist {{filesize}} MB groß. Die maximale Dateigröße beträgt {{maxFilesize}} MB.',
  dictResponseError: 'Fehler beim Hochladen. (Error {{statusCode}})',
  dictMaxFilesExceeded: 'Bitte lade maximal {{maxFiles}} Dateien hoch.',
  addRemoveLinks: true,
};

export default {
  name: 'FormulateDropzone',
  inject: ['wizardContext'],
  props: {
    context: {
      type: Object,
      required: true,
    },
    dropzoneOptions: {
      type: Object,
      default: () => ({}),
    },
    buttonText: {
      type: String,
      default: 'Datei/en auswählen',
    },
  },
  data() {
    return {
      files: isArray(this.context.model)
        ? JSON.parse(JSON.stringify(this.context.model))
        : [],
    };
  },
  watch: {
    files() {
      this.context.model = this.dropzone.getAcceptedFiles().map((file) => {
        return {
          name: file.response ? file.response.filename : file.name,
          size: file.size,
          original: file.name,
        };
      });
    },
  },
  mounted() {
    const initialFiles = this.files;

    this.dropzone = new Dropzone(this.$refs.dropzone, {
      ...defaultDropzoneOptions,
      url: this.wizardContext.uploadUrl,
      previewsContainer: this.$refs.previews,
      init() {
        initialFiles.forEach((file) => {
          this.displayExistingFile({
            name: file.original,
            size: file.size,
          });
        });
      },
      ...this.dropzoneOptions,
    });

    this.dropzone
      .on('addedfile', () => {
        this.files = [...this.dropzone.files];
      })
      .on('removedfile', () => {
        this.files = [...this.dropzone.files];
      })
      .on('success', (file, response) => {
        // eslint-disable-next-line no-param-reassign
        file.response = response;
        this.files = [...this.dropzone.files];
      });
  },
};
</script>

<style lang="scss">
@use '~tokens/sass/utils';
@use "sass:map";

.formulate-dropzone {
  $image-upload-width: 205px;
  $image-upload-height: 254px;

  position: relative;

  @at-root #{&}--image {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  @at-root #{&}__dropzone {
    position: relative;
    z-index: 2;
    height: $image-upload-height;
    border: 1px dashed #d0d7dd;
    border-radius: 5px;
    cursor: pointer;
  }

  @at-root &--has-errors &__dropzone {
    border-color: map.get(utils.$v-colors, rw-red-01);
  }

  @at-root #{&}--image #{&}__dropzone {
    width: $image-upload-width;
  }

  @at-root #{&}__files {
    display: none;
    border: 1px solid #d0d7dd;
    border-radius: 5px;
    margin-top: 15px;
  }

  @at-root #{&}--image #{&}__files {
    position: absolute;
    z-index: 3;
    top: 5px;
    right: auto;
    width: $image-upload-width - 10px;
    height: $image-upload-height - 10px;
    left: calc(50% - #{$image-upload-width} / 2 + 5px);
    border: none;
    margin-top: 0;

    .dz-preview,
    .dz-image {
      width: 100%;
      height: 100%;
    }

    .dz-image {
      display: block;
      border-radius: 5px;
      overflow: hidden;
    }

    .dz-error-mark,
    .dz-success-mark {
      position: absolute;
      height: 22px;
      width: 22px;
      top: -12px;
      right: -12px;
      margin-right: 0;
      z-index: 4;
    }

    .dz-error-message {
      align-items: center;
      justify-content: center;
      position: absolute;
      z-index: 3;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      padding: 10px;
      background-color: transparentize(#fff, 0.2);
      text-align: center;
    }

    .dz-remove {
      position: absolute;
      display: inline-block;
      width: auto;
      margin-left: 50%;
      white-space: nowrap;
      text-indent: 0;
      padding-left: 22px;
      background-position: center left !important;
      transform: translateX(-50%);
      bottom: -30px;
      color: transparentize(#4a4a4a, 0.55);
      font-size: 0.75rem;

      &:hover {
        color: transparentize(#4a4a4a, 0.4);
        text-decoration: none;
      }
    }

    .dz-details {
      display: none !important;
    }

    .dz-preview {
      position: relative;
      display: block;
      padding: 0;
    }
  }

  @at-root #{&}--has-files #{&}__files {
    display: block;
  }

  @at-root #{&}__background {
    position: absolute;
    z-index: 1;
    top: 0;
    right: 0;
    left: 0;
    height: $image-upload-height;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    transition: all 320ms;

    &::before {
      content: '';
      position: absolute;
      top: 5px;
      right: 5px;
      bottom: 5px;
      left: 5px;
      background-color: transparent;
      transition: background-color 320ms;
      border-radius: 5px;
    }
  }

  @at-root &--has-errors &__background {
    background-color: #f9f9f9;
  }

  @at-root #{&}--image #{&}__background {
    right: auto;
    left: calc(50% - #{$image-upload-width} / 2);
    width: $image-upload-width;
  }

  @at-root #{&}--image#{&}--has-files #{&}__background {
    display: none;
  }

  @at-root .dz-drag-hover ~ #{&}__background::before {
    background-color: transparentize(#4a90e2, 0.93);
  }

  @at-root .dz-drag-hover ~ #{&}__background #{&}__trigger {
    opacity: 0.28;
  }

  @at-root {
    .dz-drag-hover ~ #{&}__background #{&}__info,
    .dz-drag-hover ~ #{&}__background #{&}__background-text {
      opacity: 0.55;
    }
  }

  @at-root #{&}__background-text {
    transition: opacity 320ms;
    color: transparentize(#4a4a4a, 0.44);
    margin-bottom: 0.25rem;
  }

  @at-root #{&}__trigger {
    padding-right: utils.f-rem-calc(20);
    padding-left: utils.f-rem-calc(20);
    transition: opacity 320ms;
  }

  @at-root #{&}__info {
    transition: opacity 320ms;
    color: transparentize(#4a4a4a, 0.44);
    font-size: 0.75rem;
  }

  @at-root #{&}--image #{&}__info {
    margin-top: 7px;
    transition: none;
  }

  @at-root #{&}--image#{&}--has-files ~ #{&}__info {
    opacity: 0;
  }

  .dz-preview {
    display: flex;
    align-items: center;
    padding: 15px;
    line-height: 1.125rem;

    + .dz-preview {
      border-top: 1px solid #d0d7dd;
    }
  }

  .dz-error-mark,
  .dz-success-mark {
    width: 18px;
    height: 18px;
    align-items: center;
    margin-right: 10px;
    order: 1;

    svg {
      display: none;
    }
  }

  .dz-error-mark {
    display: none;
    background-image: url('./assets/error-circle.svg');
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
  }

  .dz-success-mark {
    background-image: url('./assets/refresh.svg');
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    opacity: 0.4;
  }

  .dz-processing:not(.dz-complete) .dz-success-mark {
    animation: rotate 1s infinite linear;
    opacity: 1;
  }

  .dz-success {
    .dz-success-mark {
      background-image: url('./assets/check-circle.svg');
      background-size: contain;
      background-repeat: no-repeat;
      background-position: center;
      opacity: 1;
    }
  }

  .dz-error {
    .dz-success-mark {
      display: none;
    }

    .dz-error-mark {
      display: block;
    }

    .dz-details {
      display: none;
    }

    .dz-error-message {
      display: block;
    }
  }

  @at-root #{&}--image .dz-error .dz-error-message {
    display: flex;
  }

  .dz-error-message {
    display: none;
    order: 2;
  }

  .dz-details {
    display: flex;
    align-items: baseline;
    order: 2;
  }

  .dz-filename {
    order: 1;
    font-weight: 500;
    margin-right: 10px;
  }

  .dz-size {
    order: 2;
    font-size: 0.75rem;
    font-weight: 400;
    color: #ccc;
  }

  .dz-remove {
    display: block;
    order: 3;
    margin-left: auto;
    width: 18px;
    height: 18px;
    text-indent: -1000em;
    background-color: map.get(utils.$v-colors, rw-grey-04);
    mask-image: url('./assets/trashcan.svg');
    mask-size: contain;
    mask-repeat: no-repeat;
    mask-position: center;
    transition: all 320ms;

    &:hover {
      background-color: transparentize(
        map.get(utils.$v-colors, rw-grey-04),
        0.5
      );
    }
  }

  .dz-image {
    display: none;
  }
}

@keyframes rotate {
  0% {
    transform: rotateZ(0deg);
  }
  100% {
    transform: rotateZ(360deg);
  }
}
</style>
